import { useEffect, useRef, useState } from 'react';

// A small animated friend who wanders the sidebar keyline above the studio
// switcher. He walks, pauses, and occasionally flips around. Pure cosmetic.

const DOOD_PX = 20;
const STRIP_PX = 24; // a bit of padding around the dood
// Tick rate: short enough to feel smooth, long enough to be cheap.
const TICK_MS = 80;
// Walking speed in pixels per tick.
const WALK_SPEED = 0.6;
// Average dwell time per phase (in ticks). The dood picks a random
// duration around these means each time he changes phase.
const WALK_TICKS_MEAN = 80; // ~6.4s at 80ms
const REST_TICKS_MEAN = 40; // ~3.2s at 80ms

type Phase = 'walking' | 'resting';

function randomDuration(meanTicks: number): number {
  // Range [0.5x, 1.5x] for a soft randomness without long stalls.
  return Math.round(meanTicks * (0.5 + Math.random()));
}

export function WanderingDood(): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [x, setX] = useState(0);
  const [facing, setFacing] = useState<1 | -1>(1);
  const phaseRef = useRef<Phase>('walking');
  const ticksLeftRef = useRef<number>(randomDuration(WALK_TICKS_MEAN));
  const dirRef = useRef<1 | -1>(1);

  useEffect(() => {
    const handle = window.setInterval(() => {
      const container = containerRef.current;
      if (container === null) return;
      const maxX = Math.max(0, container.clientWidth - DOOD_PX);

      ticksLeftRef.current -= 1;
      if (ticksLeftRef.current <= 0) {
        // Flip phase. 70% chance after a walk to take a rest, 30% to keep
        // walking but flip direction. After a rest, always start walking
        // again — sometimes in a new direction.
        if (phaseRef.current === 'walking') {
          if (Math.random() < 0.7) {
            phaseRef.current = 'resting';
            ticksLeftRef.current = randomDuration(REST_TICKS_MEAN);
          } else {
            dirRef.current = (dirRef.current * -1) as 1 | -1;
            setFacing(dirRef.current);
            ticksLeftRef.current = randomDuration(WALK_TICKS_MEAN);
          }
        } else {
          phaseRef.current = 'walking';
          if (Math.random() < 0.5) {
            dirRef.current = (dirRef.current * -1) as 1 | -1;
            setFacing(dirRef.current);
          }
          ticksLeftRef.current = randomDuration(WALK_TICKS_MEAN);
        }
      }

      if (phaseRef.current !== 'walking') return;

      setX((prev) => {
        let next = prev + WALK_SPEED * dirRef.current;
        if (next < 0) {
          next = 0;
          dirRef.current = 1;
          setFacing(1);
        } else if (next > maxX) {
          next = maxX;
          dirRef.current = -1;
          setFacing(-1);
        }
        return next;
      });
    }, TICK_MS);
    return () => window.clearInterval(handle);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden"
      style={{ height: STRIP_PX }}
      aria-hidden
    >
      <img
        src="/dood.gif"
        alt=""
        className="absolute bottom-0 select-none pointer-events-none"
        style={{
          width: DOOD_PX,
          height: DOOD_PX,
          transform: `translateX(${x}px) scaleX(${facing})`,
          transformOrigin: 'center',
          imageRendering: 'pixelated',
        }}
        draggable={false}
      />
    </div>
  );
}
